import { useEffect, useState } from 'react';
import Dialog from '../../../shared/components/Dialog/Dialog';
import Alert from '../../../shared/components/Alert/Alert';
import {
  getInterventionLdaPublicationTarget,
  getLdaPublicationTarget,
  publishInterventionLdaFile,
  publishLdaFile,
} from '../lda.api';
import './LdaPublicationDialog.scss';

const getStageLabel = (stage) => {
  if (stage === 'working') return 'Fichier de travail';
  if (stage === 'validation') return 'Fichier pour validation';
  return 'Livrable';
};

const getActionLabel = (target, loading) => {
  if (loading) return 'Publication…';
  if (!target) return 'Publier';
  if (target.existingDocumentId) {
    return target.stage === 'working' ? 'Mettre à jour le fichier' : 'Remplacer le fichier';
  }
  if (target.stage === 'working') return 'Publier le fichier de travail';
  if (target.stage === 'validation') return 'Publier pour validation';
  return 'Publier le livrable';
};

export default function LdaPublicationDialog({
  open,
  row,
  projectId = null,
  interventionId = null,
  onClose,
  onPublished,
}) {
  const [target, setTarget] = useState(null);
  const [file, setFile] = useState(null);
  const [loadingTarget, setLoadingTarget] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    if (!open || !row?.id || (!projectId && !interventionId)) return undefined;

    let active = true;
    setTarget(null);
    setFile(null);
    setSuccess(null);
    setError('');
    setLoadingTarget(true);

    const loadTarget = async () => {
      try {
        const data = projectId
          ? await getLdaPublicationTarget(projectId, row.id)
          : await getInterventionLdaPublicationTarget(interventionId, row.id);
        if (active) setTarget(data);
      } catch (requestError) {
        if (active) setError(requestError.message || 'Impossible de déterminer le dossier GED cible.');
      } finally {
        if (active) setLoadingTarget(false);
      }
    };

    loadTarget();
    return () => { active = false; };
  }, [open, row?.id, projectId, interventionId]);

  const publish = async () => {
    if (!file || !row?.id || (!projectId && !interventionId)) return;

    try {
      setPublishing(true);
      setError('');
      const result = projectId
        ? await publishLdaFile(projectId, row.id, file)
        : await publishInterventionLdaFile(interventionId, row.id, file);
      setSuccess(result);
      setFile(null);
      setTarget((current) => current ? { ...current, existingDocumentId: result.documentId } : current);
      onPublished?.(result);
    } catch (requestError) {
      setError(requestError.message || 'La publication dans NextCloud a échoué.');
    } finally {
      setPublishing(false);
    }
  };

  const close = () => {
    if (publishing) return;
    onClose?.();
  };

  const actions = (
    <>
      <button className="basic-button" type="button" onClick={close} disabled={publishing}>
        {success ? 'Fermer' : 'Annuler'}
      </button>
      {!success && (
        <button
          className="basic-button active"
          type="button"
          onClick={publish}
          disabled={!target || !file || publishing || loadingTarget}
        >
          {getActionLabel(target, publishing)}
        </button>
      )}
    </>
  );

  return (
    <Dialog
      open={Boolean(open)}
      onClose={close}
      title={`Publication GED — ${row?.title || row?.codeGed || 'Livrable'}`}
      size="medium"
      loading={publishing}
      actions={actions}
    >
      <div className="lda-publication-dialog">
        {loadingTarget && <p>Recherche du dossier de destination…</p>}

        {target && (
          <div className="lda-publication-dialog__summary">
            <div><span>Type</span><strong>{getStageLabel(target.stage)}</strong></div>
            <div><span>Document</span><strong>{target.codeGed}</strong></div>
            <div><span>Indice</span><strong>{target.indice}</strong></div>
            <div><span>Destination</span><code>{target.targetFolderPath}</code></div>
          </div>
        )}

        {error && <Alert severity="error">{error}</Alert>}

        {success && (
          <Alert severity="success">
            Fichier publié dans la GED : {success.fileName}
          </Alert>
        )}

        {target && !success && (
          <label className="lda-publication-dialog__file">
            <span>Sélectionner le fichier à publier</span>
            <input
              type="file"
              onChange={(event) => setFile(event.target.files?.[0] || null)}
              disabled={publishing}
            />
            {file && <small>{file.name}</small>}
          </label>
        )}

        {success?.openUrl && (
          <button
            className="basic-button"
            type="button"
            onClick={() => window.open(success.openUrl, '_blank', 'noopener,noreferrer')}
          >
            Ouvrir dans NextCloud
          </button>
        )}
      </div>
    </Dialog>
  );
}
